import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import Loader from "../../components/Loader";
import Sidebar from "../Dashboard/Sidebar";
import Topbar from "../Dashboard/Topbar";
// import { getAllSlots } from "../../api/slots";
import { getAllPatients } from "../../api/patient";
import { createAppointment, getAppointmentById } from "../../api/appointment";
import { getClinicsByUser } from "../../api/clinic";

const normalizeTimeTo24h = (raw) => {
  if (!raw || typeof raw !== "string") return null;
  let value = raw.trim();
  if (!value) return null;

  // If we get a range like "09:00 AM - 10:00 AM", take the first part
  if (value.includes("-")) {
    value = value.split("-")[0].trim();
  }

  const meridiemMatch = value.match(/(AM|PM)$/i);
  if (meridiemMatch) {
    const meridiem = meridiemMatch[1].toUpperCase();
    const [timePart] = value.split(/\s+/);
    const [rawHours, rawMinutes = "00"] = timePart.split(":");
    let hours = parseInt(rawHours, 10);
    if (Number.isNaN(hours)) return null;
    if (meridiem === "PM" && hours < 12) hours += 12;
    if (meridiem === "AM" && hours === 12) hours = 0;
    const formattedHours = String(hours).padStart(2, "0");
    return `${formattedHours}:${rawMinutes.padStart(2, "0")}`;
  }

  // Already 24h (HH:mm or HH:mm:ss)
  const numericParts = value.split(":");
  if (numericParts.length >= 2) {
    const [hours, minutes] = numericParts;
    if (!Number.isNaN(Number(hours)) && !Number.isNaN(Number(minutes))) {
      return `${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}`;
    }
  }
  return null;
};

const buildSlotTimestamp = (slot) => {
  const rawDate =
    slot.date ||
    slot.slotDate ||
    slot.slot_date ||
    slot.dayDate ||
    slot.dateValue ||
    "";

  if (!rawDate) return null;
  const datePart = rawDate.includes("T") ? rawDate.split("T")[0] : rawDate;

  const rawStartTime =
    slot.startTime ||
    slot.start_time ||
    slot.start ||
    slot.startDateTime ||
    slot.timeRange ||
    slot.label ||
    "";

  const timePart = normalizeTimeTo24h(rawStartTime) || "00:00";
  const hasSeconds = timePart.split(":").length === 3;
  const composedTime = hasSeconds ? timePart : `${timePart}:00`;
  const isoCandidate = `${datePart}T${composedTime}`;
  const date = new Date(isoCandidate);
  return Number.isNaN(date.getTime()) ? null : date.getTime();
};

const AppointmentPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const [loading, setLoading] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [clinicsLoading, setClinicsLoading] = useState(false);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [fetchingAppointment, setFetchingAppointment] = useState(false);
  const [slots, setSlots] = useState([]);
  const [allSlotsCache, setAllSlotsCache] = useState([]);
  const [clinics, setClinics] = useState([]);
  const fetchedSlotsRef = useRef(false);
  const fetchedClinicsRef = useRef(false);
  const [formData, setFormData] = useState({
    appointmentDate: "",
    appointmentTime: "",
    clinicId: "",
    patientId: "",
    slotId: "",
    status: "SCHEDULED",
    notes: "",
  });
  const [errors, setErrors] = useState({
    appointmentDate: "",
    appointmentTime: "",
    clinicId: "",
    patientId: "",
    slotId: "",
  });

  const [patients, setPatients] = useState([]);

  // Helper: format Date to 'YYYY-MM-DDTHH:mm' for datetime-local
  const formatDateTimeLocal = (date) => {
    try {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return "";
    }
  };

  // Fetch appointment data if in edit mode
  useEffect(() => {
    if (!isEditMode || !id) return;

    const fetchAppointment = async () => {
      setFetchingAppointment(true);
      try {
        const res = await getAppointmentById(id);
        // Handle array response - get first element
        const dataArray = res?.data?.data || [];
        const appointment = Array.isArray(dataArray) && dataArray.length > 0
          ? dataArray[0]
          : (res?.data || {});

        // Parse and split date and time
        const appointmentDate = appointment.date || appointment.appointmentDate;
        let formattedDate = "";
        let formattedTime = "";
        // if (appointmentDate) {
        //   try {
        //     const date = new Date(appointmentDate);
        //     // Format date as YYYY-MM-DD
        //     const year = date.getFullYear();
        //     const month = String(date.getMonth() + 1).padStart(2, "0");
        //     const day = String(date.getDate()).padStart(2, "0");
        //     formattedDate = `${year}-${month}-${day}`;
        //     // Format time as HH:mm
        //     const hours = String(date.getHours()).padStart(2, "0");
        //     const minutes = String(date.getMinutes()).padStart(2, "0");
        //     formattedTime = `${hours}:${minutes}`;
        //   } catch (e) {
        //     formattedDate = "";
        //     formattedTime = "";
        //   }
        // }
        if (appointmentDate) {
          try {
            const date = new Date(appointmentDate);

            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            formattedDate = `${year}-${month}-${day}`;

            // Only set time if backend actually stored time
            if (appointment.appointmentTime) {
              formattedTime = appointment.appointmentTime;
            } else {
              formattedTime = "";
            }

          } catch (e) {
            formattedDate = "";
            formattedTime = "";
          }
        }

        // If appointmentTime exists separately, use it
        if (appointment.appointmentTime) {
          formattedTime = appointment.appointmentTime;
        }

        setFormData({
          appointmentDate: formattedDate,
          appointmentTime: formattedTime || null,
          clinicId: appointment.clinicId || "",
          patientId: appointment.patientId || "",
          slotId: appointment.slotId || "",
          status: appointment.status || "SCHEDULED",
          notes: appointment.notes || "",
        });
      } catch (err) {
        toast.error("Failed to load appointment details");
        navigate("/appointments");
      } finally {
        setFetchingAppointment(false);
      }
    };

    fetchAppointment();
  }, [id, isEditMode, navigate]);

  useEffect(() => {
    const fetchPatients = async () => {
      setPatientsLoading(true);
      try {
        const res = await getAllPatients();
        const list = res?.data?.data || res?.data || [];
        const normalized = (Array.isArray(list) ? list : [])
          .map((p) => {
            const id = p.id || p._id || p.patientId || p.value || "";
            const name = p.name || p.fullName || p.label || "";
            const clinicId = p.clinicId || p.clinic?.id || "";
            const clinicName = p.clinic?.name || p.clinicName || "";
            return { id, name, clinicId, clinicName };
          })
          .filter((p) => p.id && p.name);
        setPatients(normalized);
      } catch (err) {
        setPatients([]);
      } finally {
        setPatientsLoading(false);
      }
    };
    fetchPatients();
  }, []);

  // useEffect(() => {
  //   if (fetchedSlotsRef.current) return;
  //   fetchedSlotsRef.current = true;
  //   const fetchSlots = async () => {
  //     setSlotsLoading(true);
  //     try {
  //       const res = await getAllSlots();
  //       const list = res?.data?.data || res?.data || [];
  //       const normalized = (Array.isArray(list) ? list : [])
  //         .map((s) => {
  //           const id = s.id || s._id || s.slotId || s.value || "";
  //           const clinicId = s.clinicId || s.clinic?.id || "";
  //           const clinicName = s.clinic?.name || "";
  //           const timeRange =
  //             s.label ||
  //             s.name ||
  //             s.timeRange ||
  //             (s.startTime && s.endTime ? `${s.startTime} - ${s.endTime}` : "");
  //           const day = s.day || "";
  //           const date = s.date || "";
  //           const slotTimestamp = buildSlotTimestamp(s);
  //           const labelParts = [
  //             day || date ? `${day}${day && date ? " • " : ""}${date}` : "",
  //             timeRange,
  //           ].filter(Boolean);
  //           const label = labelParts.join(" • ");
  //           return {
  //             id,
  //             label: label || timeRange,
  //             clinicId,
  //             clinicName,
  //             slotTimestamp,
  //           };
  //         })
  //         .filter((s) => s.id && s.label);
  //       const now = Date.now();
  //       const upcomingSlots = normalized.filter((slot) => {
  //         if (!slot.slotTimestamp) return true;
  //         return slot.slotTimestamp >= now;
  //       });
  //       setSlots(upcomingSlots);
  //       setAllSlotsCache(normalized);
  //     } catch (err) {
  //       setSlots([]);
  //       setAllSlotsCache([]);
  //     } finally {
  //       setSlotsLoading(false);
  //     }
  //   };
  //   fetchSlots();
  // }, []);

  useEffect(() => {
    if (
      !isEditMode ||
      !formData.slotId ||
      !allSlotsCache.length ||
      slots.some((slot) => slot.id === formData.slotId)
    ) {
      return;
    }
    const originalSlot = allSlotsCache.find(
      (slot) => slot.id === formData.slotId
    );
    if (originalSlot) {
      setSlots((prev) => [...prev, originalSlot]);
    }
  }, [isEditMode, formData.slotId, slots, allSlotsCache]);

  useEffect(() => {
    if (fetchedClinicsRef.current) return;
    fetchedClinicsRef.current = true;
    const fetchClinics = async () => {
      setClinicsLoading(true);
      try {
        const res = await getClinicsByUser();
        const list = res?.data?.data || res?.data || [];
        const normalized = (Array.isArray(list) ? list : [])
          .map((c) => {
            const id = c.id || c._id || c.clinicId || c.value || "";
            const name = c.name || c.clinicName || c.label || "";
            return { id, name };
          })
          .filter((c) => c.id && c.name);
        setClinics(normalized);
      } catch (err) {
        setClinics([]);
      } finally {
        setClinicsLoading(false);
      }
    };
    fetchClinics();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => {
      const nextFormData = { ...prevFormData, [name]: value };

      // When clinic changes, clear dependent fields so patient (and slot)
      // selection are always based on the chosen clinic
      if (name === "clinicId") {
        if (value !== prevFormData.clinicId) {
          nextFormData.patientId = "";
          nextFormData.slotId = "";
        }
        if (prevFormData.slotId) {
          const slot = slots.find((s) => s.id === prevFormData.slotId);
          if (slot && slot.clinicId && slot.clinicId !== value) {
            nextFormData.slotId = "";
          }
        }
      }

      if (name === "slotId" && value && nextFormData.clinicId) {
        const slot = slots.find((s) => s.id === value);
        if (slot && slot.clinicId && slot.clinicId !== nextFormData.clinicId) {
          nextFormData.slotId = "";
        }
      }

      return nextFormData;
    });
  };

  const toIsoString = (value) => {
    try {
      const d = new Date(value);
      return d.toISOString();
    } catch {
      return value;
    }
  };

  const selectedPatient = patients.find((p) => p.id === formData.patientId);
  // Filter patients so they depend on selected clinic
  const filteredPatients = formData.clinicId
    ? patients.filter((p) => p.clinicId === formData.clinicId)
    : patients;


  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = { appointmentDate: "", appointmentTime: "", clinicId: "", patientId: "", slotId: "" };
    if (!formData.appointmentDate)
      nextErrors.appointmentDate = "Appointment date is required";
    // if (!formData.appointmentTime)
    //   nextErrors.appointmentTime = "Appointment time is required";
    if (!formData.clinicId) nextErrors.clinicId = "Clinic is required";
    if (!formData.patientId) nextErrors.patientId = "Patient is required";
    // if (!formData.slotId) nextErrors.slotId = "Slot is required";
    setErrors(nextErrors);
    if (nextErrors.appointmentDate || nextErrors.appointmentTime || nextErrors.clinicId)
      return;
    setLoading(true);
    try {
      const payload = {
        appointmentDate: formData.appointmentDate, // YYYY-MM-DD format
        appointmentTime: formData.appointmentTime || null, // HH:mm format
        clinicId: formData.clinicId,
        doctorId: localStorage.getItem("userId") || "",
        // slotId: formData.slotId,
        patientId: formData.patientId,
        treatmentId: "",
        treatmentPlanId: "",
        notes: formData.notes || "",
        status: formData.status,
      };

      // Add appointment ID if in edit mode
      if (isEditMode && id) {
        payload.id = id;
      }

      await createAppointment(payload);
      toast.success(isEditMode ? "Appointment updated successfully!" : "Appointment scheduled successfully!");
      setTimeout(() => navigate("/appointments"), 800);
    } catch (err) {
      const msg =
        err?.response?.data?.message || (isEditMode ? "Failed to update appointment" : "Failed to create appointment");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <ToastContainer position="top-right" autoClose={3000} />
      {(loading || fetchingAppointment) && <Loader />}

      {/* Sidebar */}
      <Sidebar
        isOpen={isSidebarOpen}
        toggle={() => setIsSidebarOpen(!isSidebarOpen)}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        <Topbar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

        {/* Page Content */}
        <main className="p-4 md:p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-6xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0.97, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="w-full rounded-3xl shadow-2xl bg-white p-10"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-8">
                  {isEditMode ? "Edit Appointment" : "Create Appointment"}
                </h2>
                <button
                  className="px-4 py-1 mb-2 rounded-xl bg-gray-100 text-gray-800 border border-gray-200 hover:bg-gray-200 transition flex items-center gap-2"
                  onClick={() => window.history.back()}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-4 h-4"
                    aria-hidden="true"
                  >
                    <path d="M10 19l-7-7 7-7" />
                    <path d="M3 12h18" />
                  </svg>
                  Back
                </button>
              </div>
              {/* Form */}
              <form
                noValidate
                onSubmit={handleSubmit}
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >

                {/* Clinic (select first) */}
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Clinic <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="clinicId"
                    value={formData.clinicId}
                    onChange={handleChange}
                    required
                    disabled={clinicsLoading}
                    className={`w-full mt-1 p-2 border rounded-lg ${errors.clinicId
                      ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                      : ""
                      }`}
                  >
                    {clinicsLoading ? (
                      <option value="">Loading clinics...</option>
                    ) : (
                      <>
                        <option value="" disabled>
                          Select Clinic
                        </option>
                        {clinics.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                  {errors.clinicId && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.clinicId}
                    </p>
                  )}
                </div>

                {/* Patient (filtered by selected clinic) */}
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Patient <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="patientId"
                    value={formData.patientId}
                    onChange={handleChange}
                    required
                    disabled={patientsLoading || !formData.clinicId}
                    className={`w-full mt-1 p-2 border rounded-lg ${errors.patientId
                      ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                      : ""
                      }`}
                  >
                    {patientsLoading ? (
                      <option value="">Loading Patients...</option>
                    ) : !formData.clinicId ? (
                      <option value="">Select clinic first</option>
                    ) : filteredPatients.length === 0 ? (
                      <option value="" disabled>
                        Please add patient in clinic..
                      </option>
                    ) : (
                      <>
                        <option value="" disabled>
                          Select Patient
                        </option>
                        {filteredPatients.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                  {errors.patientsLoading && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.patientsLoading}
                    </p>
                  )}
                </div>

                {/* Appointment Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Appointment Date{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="appointmentDate"
                    value={formData.appointmentDate}
                    onChange={handleChange}
                    required
                    min={!isEditMode ? new Date().toISOString().split('T')[0] : undefined}
                    className={`w-full mt-1 p-2 border rounded-lg ${errors.appointmentDate
                      ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                      : ""
                      }`}
                  />
                  {errors.appointmentDate && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.appointmentDate}
                    </p>
                  )}
                </div>

                {/* Appointment Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Appointment Time (24-hour format){" "}
                  </label>
                  <input
                    type="time"
                    name="appointmentTime"
                    value={formData.appointmentTime}
                    onChange={handleChange}
                    required
                    step="60"
                    className={`w-full mt-1 p-2 border rounded-lg ${errors.appointmentTime
                      ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                      : ""
                      }`}
                  />
                  {errors.appointmentTime && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.appointmentTime}
                    </p>
                  )}
                </div>



                {/* Slot */}
                {/* <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Slot <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="slotId"
                    value={formData.slotId}
                    onChange={handleChange}
                    required
                    disabled={slotsLoading}
                    className={`w-full mt-1 p-2 border rounded-lg ${errors.slotId
                      ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                      : ""
                      }`}
                  >
                    {slotsLoading ? (
                      <option value="">Loading slots...</option>
                    ) : (
                      <>
                        <option value="" disabled>
                          {activeClinicId
                            ? "Select Slot"
                            : "Select a patient or clinic first"}
                        </option>
                        {filteredSlots.length > 0 ? (
                          filteredSlots.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.label}
                            </option>
                          ))
                        ) : (
                          <option value="" disabled>
                            {activeClinicId
                              ? "No slots available for selected clinic"
                              : "Please select a clinic"}
                          </option>
                        )}
                      </>
                    )}
                  </select>
                  {errors.slotId && (
                    <p className="text-red-500 text-sm mt-1">{errors.slotId}</p>
                  )}
                </div> */}

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full mt-1 p-2 border rounded-lg"
                  >
                    <option value="SCHEDULED">Scheduled</option>
                    <option value="COMPLETED">Completed</option>
                    <option value="CANCELLED">Cancelled</option>
                  </select>
                </div>
                {/* Notes */}
                <div  >
                  <label className="block text-sm font-medium text-gray-600">
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Add any notes (optional)"
                    className="w-full mt-1 p-2 border rounded-lg"
                  />
                </div>





                {/* Buttons */}
                <div className="col-span-1 md:col-span-2 flex justify-end gap-4 pt-6">
                  <button
                    type="reset"
                    onClick={() =>
                      navigate("/appointments") &&
                      setFormData({
                        appointmentDate: "",
                        appointmentTime: "",
                        clinicId: "",
                        slotId: "",
                        status: "SCHEDULED",
                        notes: "",
                      })
                    }
                    className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2   text-white rounded-lg  disabled:opacity-60"
                  >
                    Save
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default AppointmentPage;
